// The avatar is drawn the way the landing draws its faces: a signed distance
// field, liquid domain warp, a neon outline with a chromatic split, and smoke
// hugging the shape. OMNIU_GLSL supplies the noise and glow helpers.
//
// AVATAR_GLSL is a library: every function takes the avatar's parts as
// arguments, so one pass can draw several different avatars. Parts come as two
// ivec3s (see partIndices in catalog.ts):
//   idn = (face, eyes, mouth)       identity
//   sty = (hair, accessory, aura)   style
// Positions are in unit body space: q = (p - bodyCentre) / R, the body is
// roughly radius 1 around the origin. Distances come back in that space too;
// multiply by R for screen units. Include it after OMNIU_GLSL.
import { OMNIU_GLSL } from '../../glsl.ts';

export const AVATAR_GLSL = `
const vec3 AVATAR_CYAN = vec3(0.22, 0.62, 1.0);
const vec3 AVATAR_VIOLET = vec3(0.58, 0.32, 1.0);
const vec3 AVATAR_MAGENTA = vec3(0.82, 0.30, 0.95);
const vec3 AVATAR_BG = vec3(0.012, 0.01, 0.035);

mat2 ambRot(float a) { float c = cos(a), s = sin(a); return mat2(c, s, -s, c); }

// Capsule whose radius tapers from ra at a to rb at b.
float ambTaper(vec2 p, vec2 a, vec2 b, float ra, float rb) {
  vec2 pa = p - a, ba = b - a;
  float h = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0);
  return length(pa - ba * h) - mix(ra, rb, h);
}

// A tapered stroke along a quadratic curve a -> b bending toward c.
float ambTongue(vec2 p, vec2 a, vec2 c, vec2 b, float ra, float rb) {
  float d = 1e9;
  vec2 prev = a;
  for (int i = 1; i <= 6; i++) {
    float t1 = float(i) / 6.0, t0 = float(i - 1) / 6.0;
    vec2 q = mix(mix(a, c, t1), mix(c, b, t1), t1);
    d = min(d, ambTaper(p, prev, q, mix(ra, rb, t0), mix(ra, rb, t1)));
    prev = q;
  }
  return d;
}

float ambLobes(vec2 p, int n, float dist, float r, float k, float phase) {
  float d = 1e9;
  for (int i = 0; i < 6; i++) {
    if (i >= n) break;
    float a = phase + 6.28318 * float(i) / float(n);
    d = smin(d, length(p - dist * vec2(cos(a), sin(a))) - r, k);
  }
  return d;
}

float ambBox(vec2 p, vec2 b) {
  vec2 d = abs(p) - b;
  return length(max(d, 0.0)) + min(max(d.x, d.y), 0.0);
}

float ambRoundBox(vec2 p, vec2 b, float r) { return ambBox(p, b - r) - r; }

// A pointed lens, half-width w and half-height h, lying along x.
float ambLens(vec2 p, float w, float h) {
  float r = (w * w + h * h) / (2.0 * h);
  return max(length(p - vec2(0.0, h - r)), length(p - vec2(0.0, r - h))) - r;
}

float ambStar5(vec2 p, float r, float rf) {
  const vec2 k1 = vec2(0.809016994375, -0.587785252292);
  const vec2 k2 = vec2(-k1.x, k1.y);
  p.x = abs(p.x);
  p -= 2.0 * max(dot(k1, p), 0.0) * k1;
  p -= 2.0 * max(dot(k2, p), 0.0) * k2;
  p.x = abs(p.x);
  p.y -= r;
  vec2 ba = rf * vec2(-k1.y, k1.x) - vec2(0.0, 1.0);
  float h = clamp(dot(p, ba) / dot(ba, ba), 0.0, r);
  return length(p - ba * h) * sign(p.y * ba.x - p.x * ba.y);
}

// Face shapes. Shapes with tails trail them left: everyone is hurtling
// rightward through time.
float avatarFace(vec2 p, float t, int shape) {
  if (shape == 0) return length(p) - 1.0;
  if (shape == 1) {
    float d = length(p - vec2(-0.34, 0.3)) - 0.6;
    d = smin(d, length(p - vec2(0.36, 0.34)) - 0.56, 0.4);
    d = smin(d, length(p - vec2(0.42, -0.28)) - 0.6, 0.4);
    d = smin(d, length(p - vec2(-0.4, -0.3)) - 0.62, 0.4);
    return d;
  }
  if (shape == 2) {
    // The drop's point trails behind.
    float d = length(p) - 0.9;
    float sw = 0.06 * sin(t * 2.2);
    return smin(d, ambTongue(p, vec2(-0.4, 0.3), vec2(-1.2, 0.3), vec2(-1.45, 0.85 + sw), 0.55, 0.03), 0.35);
  }
  if (shape == 3) {
    // The flames lick up and back.
    float d = length(p) - 0.88;
    float f = 0.06 * sin(t * 3.1), g = 0.06 * sin(t * 2.3 + 1.0);
    d = smin(d, ambTongue(p, vec2(0.1, 0.5), vec2(-0.15, 1.0), vec2(-0.55 + f, 1.4), 0.36, 0.04), 0.3);
    d = smin(d, ambTongue(p, vec2(-0.25, 0.4), vec2(-0.75, 0.75), vec2(-1.1 + g, 1.05), 0.3, 0.04), 0.3);
    d = smin(d, ambTongue(p, vec2(-0.45, 0.15), vec2(-1.0, 0.35), vec2(-1.35, 0.6 + f), 0.26, 0.04), 0.3);
    return d;
  }
  if (shape == 4) return ambLobes(p, 4, 0.44, 0.6, 0.18, 0.785);
  if (shape == 5) return smin(length(p) - 0.72, ambLobes(p, 6, 0.55, 0.45, 0.12, 0.26), 0.15);
  if (shape == 6) {
    // The comet's tail streams out flat behind.
    float d = length(p) - 0.84;
    float f = 0.06 * sin(t * 3.0), g = 0.06 * sin(t * 3.7 + 2.0), h = 0.05 * sin(t * 2.6 + 4.0);
    d = smin(d, ambTongue(p, vec2(-0.3, 0.35), vec2(-1.0, 0.42), vec2(-1.7, 0.36 + f), 0.45, 0.02), 0.3);
    d = smin(d, ambTongue(p, vec2(-0.3, -0.3), vec2(-1.0, -0.3), vec2(-1.7, -0.22 + g), 0.42, 0.02), 0.3);
    d = smin(d, ambTongue(p, vec2(-0.3, 0.0), vec2(-1.1, 0.05), vec2(-1.95, 0.06 + h), 0.45, 0.02), 0.3);
    return d;
  }
  return length(p) - 0.92;
}

// Where the face features sit: the visual center of mass, which drifts back
// toward the tail on shapes that have one.
vec2 avatarFeatureOffset(int shape) {
  if (shape == 6) return vec2(-0.2, 0.0);
  if (shape == 3) return vec2(-0.1, 0.05);
  return vec2(0.0);
}

// Feature space: body space moved to the features, shifted by look (-1..1).
vec2 avatarFeatureSpace(vec2 q, vec2 look, int shape) {
  return q - avatarFeatureOffset(shape) - look * 0.07;
}

// Both eyes in feature space, mirrored across x. blink squashes them shut.
float avatarEyes(vec2 q, float t, float blink, int kind) {
  const float S = 1.45;  // eye size relative to the shapes below
  vec2 e = vec2(abs(q.x) - 0.36, q.y - 0.12) / S;
  if (kind == 0 || kind == 3) { e.y /= blink; }
  float d;
  if (kind == 0) d = (length(e / vec2(0.065, 0.1)) - 1.0) * 0.065;
  else if (kind == 1) d = ambLens(ambRot(-0.12) * e, 0.15, 0.05);
  else if (kind == 2) d = sdArc(e + vec2(0.0, 0.05), vec2(sin(1.05), cos(1.05)), 0.13, 0.024);
  else if (kind == 3) d = ambTaper(e, vec2(0.0, -0.07), vec2(0.0, 0.07), 0.04, 0.04);
  else if (kind == 4) {
    vec2 a = abs(e) / 0.13;
    d = (pow(sqrt(a.x) + sqrt(a.y), 2.0) - 1.0) * 0.045;
  } else if (kind == 5) {
    float r = length(e), a = atan(e.y, e.x * sign(q.x)) + t * 0.8;
    float arm = 0.042;
    d = abs(mod(r - arm * a / 6.28318, arm) - arm * 0.5) - 0.009;
    d = max(d, r - 0.12);
  } else if (kind == 6) {
    d = ambLens(ambRot(0.5) * (e - vec2(0.02, 0.0)), 0.13, 0.055);
  } else {
    // Blocky arch, pixel style.
    d = min(ambBox(e - vec2(0.0, 0.045), vec2(0.085, 0.022)),
            ambBox(vec2(abs(e.x) - 0.064, e.y + 0.005), vec2(0.022, 0.05)));
  }
  return d * S;
}

// The mouth in feature space, centered below the eyes. smile is the landing's
// smile angle: 0.75 at rest, about 1.35 for a wide "aha". The smile takes it as
// its arc; the swoosh, open mouth and smirk deepen with it.
float avatarMouth(vec2 q, float t, float smile, int kind) {
  const float S = 1.3;
  vec2 m = (q - vec2(0.0, -0.32)) / S;
  float k = smile / 0.75;
  float d;
  if (kind == 0) d = sdArc(vec2(m.x, 0.2 - m.y), vec2(sin(smile), cos(smile)), 0.28, 0.022);
  else if (kind == 1 || kind == 7) {
    // Waves: two small humps, or one wide tilde.
    float f = kind == 1 ? 31.4 : 15.7, a = kind == 1 ? 0.022 : 0.04;
    float mx = clamp(m.x, -0.2, 0.2);
    float y = a * sin(mx * f + (kind == 1 ? t * 1.5 : 0.0));
    float slope = a * f * cos(mx * f);
    d = length(vec2(m.x - mx, (m.y - y) / sqrt(1.0 + slope * slope))) - 0.02;
  } else if (kind == 2) {
    d = min(min(sdSeg(m, vec2(-0.21, 0.03), vec2(-0.105, -0.035)), sdSeg(m, vec2(-0.105, -0.035), vec2(0.0, 0.03))),
            min(sdSeg(m, vec2(0.0, 0.03), vec2(0.105, -0.035)), sdSeg(m, vec2(0.105, -0.035), vec2(0.21, 0.03)))) - 0.017;
  } else if (kind == 3) {
    // A brush stroke: thick in the middle, tapering to both tips.
    vec2 mid = vec2(-0.02, -0.03 * k);
    d = min(ambTongue(m, mid, vec2(-0.13, -0.05 * k), vec2(-0.25, 0.02), 0.032, 0.004),
            ambTongue(m, mid, vec2(0.12, -0.02 * k), vec2(0.25, 0.06), 0.032, 0.004));
  } else if (kind == 4) {
    float r = 0.1 * mix(1.0, k, 0.4);
    float half_ = max(length(m - vec2(0.0, 0.035)) - r, m.y - 0.035);
    d = abs(half_) - 0.016;
  } else if (kind == 5) {
    d = min(ambTongue(m, vec2(-0.2, 0.0), vec2(0.0, -0.09 * k), vec2(0.19, 0.0), 0.012, 0.02),
            sdSeg(m, vec2(0.19, 0.0), vec2(0.225, 0.045)) - 0.016);
  } else {
    d = sdArc(vec2(abs(m.x) - 0.075, m.y + 0.03), vec2(sin(1.35), cos(1.35)), 0.075, 0.018);
  }
  return d * S;
}

// A lock of hair: thick at the root, tapering to a point.
float ambLock(vec2 q, vec2 a, vec2 c, vec2 b, float ra) { return ambTongue(q, a, c, b, ra, 0.015); }

// Hair is a small flourish sprouting from the top of the mass, part of the same
// liquid body. It leans back (left). 1e3 when bald.
float avatarHair(vec2 q, float t, int kind) {
  if (kind <= 0) return 1e3;
  float sw = 0.04 * sin(t * 1.6), sw2 = 0.04 * sin(t * 2.1 + 1.0);
  float d = 1e3;
  if (kind == 1) {        // tuft
    d = ambLock(q, vec2(0.0, 0.85), vec2(0.0, 1.25), vec2(-0.12 + sw, 1.48), 0.15);
    d = min(d, ambLock(q, vec2(-0.08, 0.85), vec2(-0.25, 1.2), vec2(-0.5 + sw2, 1.3), 0.13));
    d = min(d, ambLock(q, vec2(0.08, 0.85), vec2(0.22, 1.2), vec2(0.28 + sw2, 1.38), 0.12));
  } else if (kind == 2) { // spikes
    for (int i = 0; i < 4; i++) {
      float x = mix(-0.32, 0.32, float(i) / 3.0);
      float h = 1.32 + 0.12 * sin(float(i) * 2.4 + 1.0);
      d = min(d, ambLock(q, vec2(x, 0.82), vec2(x - 0.04, 1.1), vec2(x - 0.14 + sw, h), 0.11));
    }
  } else if (kind == 3) { // bun
    d = length(q - vec2(-0.05 + sw * 0.3, 1.2)) - 0.24;
  } else if (kind == 4) { // horns
    d = ambLock(q, vec2(-0.42, 0.72), vec2(-0.68, 1.05), vec2(-0.55 + sw, 1.38), 0.13);
    d = min(d, ambLock(q, vec2(0.42, 0.72), vec2(0.68, 1.05), vec2(0.55 + sw, 1.38), 0.13));
  } else if (kind == 5) { // sprout
    d = ambTaper(q, vec2(0.0, 0.85), vec2(-0.02 + sw * 0.3, 1.2), 0.05, 0.035);
    d = min(d, ambLens(ambRot(-0.5 - sw) * (q - vec2(-0.2, 1.32)), 0.2, 0.08));
    d = min(d, ambLens(ambRot(0.6 - sw) * (q - vec2(0.17, 1.3)), 0.17, 0.07));
  } else if (kind == 6) { // antenna
    vec2 tip = vec2(-0.18 + sw, 1.42);
    d = min(ambTaper(q, vec2(0.0, 0.8), tip, 0.045, 0.025), length(q - tip) - 0.1);
  } else {                // wild
    for (int i = 0; i < 5; i++) {
      float fi = float(i);
      float a = mix(1.3, 2.05, fi / 4.0) + 0.08 * sin(fi * 3.7);
      vec2 dir = vec2(cos(a), sin(a));
      float len = 1.28 + 0.14 * hash(vec2(fi, 4.0));
      d = min(d, ambLock(q, dir * 0.85, dir * 1.1 - vec2(0.08, 0.0), dir * len - vec2(0.18 + (mod(fi, 2.0) == 0.0 ? sw : sw2), 0.0), 0.1));
    }
  }
  return d;
}

// Where hair grows from. The flame's tilts forward off the top of its head;
// the comet's sits on top of its head.
vec2 avatarHairSpace(vec2 q, int shape) {
  if (shape == 3) return ambRot(0.45) * q;
  if (shape == 6) return q;
  return q - avatarFeatureOffset(shape);
}

// The silhouette: face and hair as one liquid body, one outline.
float avatarBody(vec2 q, float t, ivec3 idn, ivec3 sty) {
  return smin(avatarFace(q, t, idn.x), min(avatarHair(avatarHairSpace(q, idn.x), t, sty.x), 10.0), 0.08);
}

// Eyes, mouth and line accessories (glasses, monocle, star glasses) as one
// distance, for avatarFeatures.
float ambAccLines(vec2 fq, int acc) {
  vec2 e = vec2(abs(fq.x) - 0.36, fq.y - 0.12);
  float bridge = sdArc(vec2(fq.x, fq.y - 0.08), vec2(sin(0.8), cos(0.8)), 0.2, 0.02);
  if (acc == 1) return min(abs(length(e) - 0.2) - 0.022, bridge);
  if (acc == 3) {
    // Monocle on the leading (right) eye, its chain falling away.
    vec2 m = fq - vec2(0.36, 0.12);
    return min(abs(length(m) - 0.21) - 0.024, sdSeg(fq, vec2(0.5, -0.04), vec2(0.62, -0.55)) - 0.01);
  }
  if (acc == 5) return min(abs(ambStar5(e, 0.25, 0.5)) - 0.02, bridge);
  return 1e3;
}

float avatarLines(vec2 q, float t, float blink, vec2 look, float smile, ivec3 idn, ivec3 sty) {
  vec2 fq = avatarFeatureSpace(q, look, idn.x);
  return min(min(avatarEyes(fq, t, blink, idn.y), avatarMouth(fq, t, smile, idn.z)), ambAccLines(fq, sty.y));
}

// The blink the prototype uses: open, with a quick blink every few seconds.
float avatarBlink(float t) { return max(smoothstep(0.0, 0.03, abs(fract(t * 0.19) - 0.5)), 0.08); }

// Cyan where light enters (lower left), violet through to magenta at the far edge.
vec3 avatarPalette(vec2 q) {
  float g = dot(q, normalize(vec2(1.0, 0.9)));
  vec3 col = mix(AVATAR_CYAN, AVATAR_VIOLET, smoothstep(-1.0, 0.4, g));
  return mix(col, AVATAR_MAGENTA, smoothstep(0.6, 1.6, g) * 0.8);
}

// The calm liquid shimmer: offset p by w * 0.014 before measuring the body.
vec2 avatarWarp(vec2 p, float t) {
  return vec2(fbm(p * 3.0 + vec2(0.0, t * 0.35)), fbm(p * 3.0 + vec2(5.2, -t * 0.3))) - 0.5;
}

// The composition below takes D, the body distance in screen units (unit
// distance * R), so bodies merged with smin share one outline, glass and smoke.

float avatarInside(float D, float R) { return 1.0 - smoothstep(-R * 0.01, R * 0.01, D); }

// Neon outline with the landing's chromatic split.
vec3 avatarOutline(float D, float R, vec3 col) {
  float ad = abs(D), ca = R * 0.014;
  return col * glowLine(ad, R)
    + vec3(0.95, 0.25, 0.85) * exp(-abs(D - ca) / (R * 0.012)) * 0.45
    + vec3(0.25, 0.9, 1.0) * exp(-abs(D + ca) / (R * 0.012)) * 0.45
    + vec3(0.9, 0.95, 1.0) * (1.0 - smoothstep(0.0, R * 0.008, ad)) * 0.7;
}

// Glass: a luminous inner rim, nebula inside, a highlight along the top left.
vec3 avatarGlass(vec2 q, float D, float R, float t, vec2 w, vec3 col) {
  float inside = avatarInside(D, R);
  float neb = fbm(q * 1.6 + vec2(t * 0.05, -t * 0.04) + w * 2.0);
  vec3 glass = inside * (col * exp(D / (R * 0.22)) * 0.85
    + col * (0.1 + 0.35 * pow(neb, 2.0))
    + mix(AVATAR_VIOLET, AVATAR_MAGENTA, neb) * pow(fbm(q * 3.0 - t * 0.03), 4.0) * 0.5);
  float hl = smoothstep(0.2, 0.9, dot(normalize(q + 1e-4), normalize(vec2(-0.7, 0.75))));
  return glass + vec3(0.75, 0.85, 1.0) * exp(-abs(D + R * 0.09) / (R * 0.03)) * hl * 0.55 * inside;
}

// Smoke and ripples hugging the outside. p is the unwarped screen position.
vec3 avatarSmoke(vec2 p, float D, float R, float t, vec2 w, vec3 col) {
  float field = exp(-max(D, 0.0) / (R * 0.3));
  vec2 sp = p * 2.2 + vec2(t * 0.05, -t * 0.08) + w * 2.0;
  float sm = fbm(sp + fbm(sp * 1.7 - t * 0.1));
  float ridge = 1.0 - abs(2.0 * sm - 1.0);
  vec3 smoke = col * (pow(sm, 3.0) * 0.35 + pow(ridge, 10.0) * 0.9) * field * 0.45;
  smoke += col * smoothstep(0.55, 1.0, sin(D * 70.0 + sm * 10.0 - t * 2.0)) * exp(-abs(D) / (R * 0.3)) * 0.08;
  return smoke * (1.0 - avatarInside(D, R));
}

// Features glow near-white with the body's hue around them. L from avatarLines.
vec3 avatarFeatures(float L, float R, vec3 col) {
  return mix(vec3(0.9, 0.9, 1.0), col, 0.35) * glowFeat(L * R, R);
}

// Auras: style that travels with a person, drifting around and behind the body.
// ap is the position relative to the body centre in screen units (warped), D the
// body distance. They fade with distance so neighbours never overlap; the
// orbiting moons keep to their own path instead.
vec3 avatarAura(vec2 ap, float D, float inside, float R, float t, int kind) {
  float behind = 1.0 - inside;
  float near = exp(-max(D, 0.0) / (R * 0.9)) * behind;
  float fade = 1.0 - smoothstep(R * 0.05, R * 0.75, max(D, 0.0));
  if (kind == 1) {        // sparkles
    vec2 g = ap * 12.0, id = floor(g), f = fract(g) - 0.5;
    float h = hash(id + 2.0);
    vec2 dd = f - (vec2(hash(id + 3.1), hash(id + 7.7)) - 0.5) * 0.6;
    float tw = pow(0.5 + 0.5 * sin(t * 2.2 + h * 40.0), 4.0);
    float st = exp(-abs(dd.x) * 40.0) * exp(-abs(dd.y) * 6.0) + exp(-abs(dd.y) * 40.0) * exp(-abs(dd.x) * 6.0);
    return vec3(0.85, 0.9, 1.0) * st * tw * step(0.6, h) * near * 1.6 * fade;
  }
  if (kind == 2) {        // rain, slanted because we're moving right
    vec2 rp = vec2(ap.x - ap.y * 0.35, ap.y);
    float colm = floor(rp.x * 40.0), h = hash(vec2(colm, 5.0));
    float y = fract(rp.y * 2.5 + t * (1.0 + h) + h * 10.0);
    float streak = (1.0 - smoothstep(0.0, 0.18, y)) * (1.0 - smoothstep(0.08, 0.2, abs(fract(rp.x * 40.0) - 0.5)));
    return mix(AVATAR_CYAN, vec3(0.8, 0.9, 1.0), 0.4) * streak * step(0.45, h) * exp(-max(D, 0.0) / (R * 1.4)) * behind * 1.8 * fade;
  }
  if (kind == 3) {        // radiance
    float a = atan(ap.y, ap.x);
    float rays = pow(0.5 + 0.5 * sin(a * 14.0 + sin(a * 3.0 + t * 0.4) * 2.0), 6.0);
    return mix(vec3(1.0, 0.85, 0.6), AVATAR_CYAN, 0.25)
         * (rays * exp(-max(D, 0.0) / (R * 0.6)) * 0.5 + exp(-max(D, 0.0) / (R * 0.3)) * 0.35) * behind * fade;
  }
  if (kind == 4) {        // embers rising
    vec2 g = vec2(ap.x * 14.0, ap.y * 14.0 - t * 1.5), id = floor(g), f = fract(g) - 0.5;
    float h = hash(id + 9.0);
    vec2 o = vec2((hash(id + 1.3) - 0.5) * 0.6 + 0.15 * sin(t * 2.0 + h * 20.0), 0.0);
    float flick = 0.6 + 0.4 * sin(t * 6.0 + h * 30.0);
    return mix(AVATAR_MAGENTA, vec3(1.0, 0.7, 0.4), h) * (exp(-length(f - o) * 14.0) + (1.0 - smoothstep(0.03, 0.06, length(f - o)))) * step(0.5, h) * near * 2.2 * flick * fade;
  }
  if (kind == 5) {        // ripples
    float w = sin(D / R * 9.0 - t * 2.5);
    return mix(AVATAR_CYAN, AVATAR_VIOLET, 0.5) * smoothstep(0.85, 1.0, w) * exp(-max(D, 0.0) / (R * 0.7)) * behind * 0.8 * fade;
  }
  if (kind == 6) {        // orbiting moons, hidden while they pass behind
    vec3 o = vec3(0.0);
    for (int k = 0; k < 3; k++) {
      float ang = t * 0.8 + float(k) * 2.094;
      vec2 pos = ambRot(-0.3) * vec2(cos(ang) * 1.55, sin(ang) * 0.4) * R;
      float d = length(ap - pos);
      float hidden = step(0.0, sin(ang)) * inside;
      o += mix(AVATAR_CYAN, vec3(0.9, 0.95, 1.0), 0.5) * ((1.0 - smoothstep(R * 0.085, R * 0.1, d)) * 1.1 + exp(-d / (R * 0.08)) * 0.6) * (1.0 - hidden);
    }
    return o;
  }
  if (kind == 7) {        // snow
    vec2 g = vec2(ap.x * 10.0 + sin(ap.y * 8.0 + t) * 0.3, ap.y * 10.0 + t * 0.5), id = floor(g), f = fract(g) - 0.5;
    float h = hash(id + 4.0);
    vec2 o = (vec2(hash(id + 6.1), hash(id + 2.7)) - 0.5) * 0.6;
    return vec3(0.85, 0.9, 1.0) * (1.0 - smoothstep(0.06, 0.12, length(f - o))) * step(0.45, h) * exp(-max(D, 0.0) / (R * 1.6)) * behind * 1.4 * fade;
  }
  return vec3(0.0);
}

// Blush: soft pink on the cheeks, inside the glass.
vec3 avatarBlush(vec2 q, vec2 look, float inside, ivec3 idn, ivec3 sty) {
  if (sty.y != 6) return vec3(0.0);
  vec2 fq = avatarFeatureSpace(q, look, idn.x);
  vec2 ck = vec2(abs(fq.x) - 0.56, fq.y + 0.1) / vec2(0.17, 0.09);
  return vec3(1.0, 0.4, 0.75) * 0.45 * exp(-dot(ck, ck)) * inside;
}

// Accessories drawn as small solid neon objects (bowtie, shades, headphones):
// unit distance, 1e3 when there is none.
float avatarAccSolid(vec2 q, vec2 look, ivec3 idn, ivec3 sty) {
  int acc = sty.y;
  if (acc == 2) {
    vec2 b = q - vec2(0.0, -0.9);
    vec2 a = vec2(abs(b.x), b.y);
    float wing = max(a.x - 0.32, (abs(a.y) - (a.x + 0.02) * 0.55) / 1.14) - 0.025;
    return min(wing, length(b) - 0.075);
  }
  if (acc == 4) {
    vec2 fq = avatarFeatureSpace(q, look, idn.x);
    vec2 e = vec2(abs(fq.x) - 0.36, fq.y - 0.12);
    return min(ambRoundBox(e, vec2(0.26, 0.18), 0.1), ambBox(fq - vec2(0.0, 0.2), vec2(0.14, 0.035)));
  }
  if (acc == 7) {
    // Headphones: a band over the head, a cup on each side.
    float band = sdArc(q - vec2(0.0, 0.02), vec2(sin(1.2), cos(1.2)), 1.08, 0.045);
    float cups = ambRoundBox(vec2(abs(q.x) - 1.0, q.y + 0.02), vec2(0.14, 0.25), 0.11);
    return min(band, cups);
  }
  return 1e3;
}

// Lay a solid accessory over what's beneath it, with its own glass and outline.
// A is avatarAccSolid * R. Shades are dark glass; everything else glows through.
vec3 avatarAccessoryOver(vec3 under, float A, vec2 q, float R, int acc) {
  A = min(A, 10.0 * R);
  float accIn = 1.0 - smoothstep(-R * 0.01, R * 0.01, A);
  vec3 acol = mix(AVATAR_MAGENTA, AVATAR_VIOLET, smoothstep(-0.6, 0.6, q.y + 0.3 * q.x));
  vec3 glass = acol * (exp(min(A, 0.0) / (R * 0.05)) * 0.9 + (acc == 4 ? 0.03 : 0.2));
  return mix(under, AVATAR_BG + glass, accIn) + acol * glowLine(abs(A), R) * 0.8
       + vec3(0.9, 0.95, 1.0) * (1.0 - smoothstep(0.0, R * 0.008, abs(A))) * 0.5;
}

// The planet's ring, hidden where it passes behind the body. q in body space.
vec3 avatarRing(vec2 q, float R, float inside, int shape) {
  if (shape != 7) return vec3(0.0);
  vec2 rq = ambRot(-0.32) * q;
  vec2 ab = vec2(1.75, 0.42);
  float k0 = length(rq / ab), k1 = length(rq / (ab * ab));
  float rd = abs(k0 * (k0 - 1.0) / k1) * R;
  float behind = step(0.0, rq.y) * inside;
  vec3 rc = mix(vec3(1.0, 0.78, 0.45), AVATAR_MAGENTA, smoothstep(-1.5, 1.5, rq.x));
  return rc * (1.0 - behind) * ((1.0 - smoothstep(0.0, R * 0.02, rd)) * 0.9 + exp(-rd / (R * 0.05)) * 0.35);
}

// Framing for a square tile: the planet shrinks to fit its ring, and shapes
// with tails sit right of centre (offset in body units, multiply by R).
float avatarFrameScale(int shape) { return shape == 7 ? 0.852 : 1.0; }
vec2 avatarFrameOffset(int shape) {
  if (shape == 3) return vec2(0.22, -0.15);
  if (shape == 2) return vec2(0.3, -0.12);
  if (shape == 6) return vec2(0.38, 0.0);
  return vec2(0.0);
}
`;

// One avatar filling the canvas. With alpha, the output is premultiplied with
// no background or vignette, for drawing over other content.
const MAIN = `
uniform ivec3 uIdn;   // face, eyes, mouth
uniform ivec3 uSty;   // hair, accessory, aura

void main() {
  vec2 p = (gl_FragCoord.xy - 0.5 * uRes) / uRes.y;
  float t = uTime;
  float R = 0.27 * avatarFrameScale(uIdn.x);
  vec2 c = avatarFrameOffset(uIdn.x) * R + vec2(0.0, sin(t * 0.9) * 0.006);

  vec2 w = avatarWarp(p, t);
  vec2 pw = p + w * 0.014;
  vec2 q = (pw - c) / R;
  float D = avatarBody(q, t, uIdn, uSty) * R;
  vec3 col = avatarPalette(q);
  float inside = avatarInside(D, R);

  vec3 outc = avatarSmoke(p, D, R, t, w, col) + avatarGlass(q, D, R, t, w, col) + avatarOutline(D, R, col)
    + avatarFeatures(avatarLines(q, t, avatarBlink(t), vec2(0.0), 0.75, uIdn, uSty), R, col)
    + avatarAura(pw - c, D, inside, R, t, uSty.z)
    + avatarBlush(q, vec2(0.0), inside, uIdn, uSty);
#ifndef AVATAR_ALPHA
  outc += AVATAR_BG;
#endif
  float A = avatarAccSolid(q, vec2(0.0), uIdn, uSty) * R;
  outc = avatarAccessoryOver(outc, A, q, R, uSty.y);
  outc += avatarRing(q, R, inside, uIdn.x);

#ifdef AVATAR_ALPHA
  outc = 1.0 - exp(-outc * 1.5);
  float a = max(max(inside, 1.0 - smoothstep(-R * 0.01, R * 0.01, A)), max(outc.r, max(outc.g, outc.b)));
  gl_FragColor = vec4(outc, clamp(a, 0.0, 1.0));
#else
  outc *= 1.0 - 0.6 * dot(p, p);
  outc = 1.0 - exp(-outc * 1.5);
  outc += (hash(gl_FragCoord.xy + t) - 0.5) / 255.0;
  gl_FragColor = vec4(outc, 1.0);
#endif
}
`;

export const avatarFrag = (alpha: boolean): string => OMNIU_GLSL + AVATAR_GLSL + (alpha ? '#define AVATAR_ALPHA\n' : '') + MAIN;

export const AVATAR_FRAG = avatarFrag(false);
